/** @format */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Grid,
    Typography,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Checkbox,
    FormControlLabel,
    FormGroup,
    TextField,
    Button,
    Collapse,
} from "@mui/material";
import {
    ContentCopy,
    Share,
    Info,
    CheckCircle,
    ArrowForward,
    People,
    CalendarMonth,
    AttachMoney,
    Repeat,
    ExpandMore,
    ExpandLess,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import {
    updateSandboxMemberStatus,
    updateSandboxMemberDayStatus,
    createSandboxShareLink,
} from "../../redux/actions/sandboxAction";
import {
    selectSandboxCircle,
    selectSandboxIsMemberUpdating,
    selectSandboxIsSharing,
    selectSandboxShareResult,
} from "../../redux/selectors/sandboxSelector";
import {
    ISandboxCircle,
    ISandboxMember,
    ISandboxRound,
    ISandboxDailyContribution,
    SandboxContributionStatus,
    SandboxDayStatus,
} from "../../utility/types/sandboxTypes";
import { colors } from "../../utility/types/constant";
import {
    SandboxSectionCard,
    SandboxSectionTitle,
    SandboxStatCard,
    SandboxStatValue,
    SandboxStatLabel,
    MemberRow,
    MemberInfo,
    MemberAvatar,
    MemberName,
    MemberSubtext,
    RoundRow,
    RoundNumber,
    StatusChip,
    SandboxCTASection,
    CTATitle,
    CTASubtitle,
    CTAButton,
    CTASecondaryButton,
    SandboxDisclaimer,
    NextUpBadge,
} from "./styles";
import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

const CONTRIBUTION_STATUS_OPTIONS: SandboxContributionStatus[] = [
    "completed",
    "missed",
];

type SupportedLanguage = "en" | "es" | "fr" | "mx";

function getSupportedLanguage(language?: string): SupportedLanguage {
    const key = (language || "en").toLowerCase().split("-")[0];
    if (key === "es" || key === "fr" || key === "mx") return key;
    return "en";
}

function getLocaleFromLanguage(language: SupportedLanguage): string {
    const localeMap: Record<SupportedLanguage, string> = {
        en: "en-US",
        es: "es-ES",
        fr: "fr-FR",
        mx: "es-MX",
    };
    return localeMap[language];
}

function getQuickDemoDefaults(language: SupportedLanguage): { country: string; currency: string } {
    const localeMap: Record<SupportedLanguage, { country: string; currency: string }> = {
        en: { country: "US", currency: "usd" },
        es: { country: "ES", currency: "eur" },
        fr: { country: "FR", currency: "eur" },
        mx: { country: "MX", currency: "mxn" },
    };
    return localeMap[language];
}

function getDisplayCurrency(circle: ISandboxCircle, language: SupportedLanguage): string {
    if (circle.sandboxType === "quick_demo") {
        return getQuickDemoDefaults(language).currency;
    }
    return circle.currency || "usd";
}

function getDisplayCountry(circle: ISandboxCircle, language: SupportedLanguage): string {
    if (circle.sandboxType === "quick_demo") {
        return getQuickDemoDefaults(language).country;
    }
    return circle.country || "US";
}

function getCircleDisplayName(circle: ISandboxCircle, t: (key: string) => string): string {
    if (
        circle.sandboxType === "quick_demo" &&
        ["Sample Savings Circle", "Sample Family Savings Circle"].includes(circle.circleName)
    ) {
        return t("dashboard.quickDemoCircleName");
    }
    return circle.circleName;
}

function getMemberDisplayName(
    member: ISandboxMember,
    t: (key: string, options?: Record<string, unknown>) => string,
): string {
    if (member.displayName === "You") {
        return t("dashboard.youLabel");
    }

    const demoMemberMatch = member.displayName.match(/^Demo Member\s+(\d+)$/);
    if (demoMemberMatch) {
        return t("dashboard.demoMemberName", { number: Number(demoMemberMatch[1]) });
    }

    return member.displayName;
}


function formatCurrency(amount: number, currency = "USD", locale = "en-US"): string {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatDate(dateStr: string, locale = "en-US"): string {
    // Slice to 10 chars to handle both "YYYY-MM-DD" and "YYYY-MM-DDT..."
    const [year, month, day] = dateStr.slice(0, 10).split("-").map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function getMemberInitials(name: string): string {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
}

// ── Adjusted payout helper ───────────────────────────────────────────────
/**
 * Recomputes pool/fee/payout for a single round based on CURRENT member statuses.
 *
 * Per-day granularity (when dailyContributions present):
 *   memberShare = fullShare × paidCount / total
 *   "paid"   = confirmed payment
 *   "missed" = no payment → that fraction reduces the pool
 *
 * Binary fallback (no dailyContributions):
 *   "missed" members exclude their full share from the pool.
 */
function computeAdjustedRoundPayout(
    round: ISandboxRound,
    members: ISandboxMember[],
    platformFeePercentage: number,
): { adjustedPool: number; adjustedFee: number; adjustedPayout: number; shortfall: number } {
    const perMemberShare = round.expectedPoolAmount / members.length;
    let adjustedPool = 0;

    for (const member of members) {
        if (member.dailyContributions && member.dailyContributions.length > 0) {
            const total = member.dailyContributions.length;
            const paid = member.dailyContributions.filter((d) => d.status === "paid").length;
            adjustedPool += perMemberShare * (paid / total);
        } else {
            // Fallback: binary — missed members exclude from pool
            if (member.contributionStatus !== "missed") {
                adjustedPool += perMemberShare;
            }
        }
    }

    adjustedPool = parseFloat(adjustedPool.toFixed(2));
    const adjustedFee = parseFloat((adjustedPool * platformFeePercentage / 100).toFixed(2));
    const adjustedPayout = parseFloat((adjustedPool - adjustedFee).toFixed(2));
    const shortfall = parseFloat((round.expectedPoolAmount - adjustedPool).toFixed(2));
    return { adjustedPool, adjustedFee, adjustedPayout, shortfall };
}

// ── Day contribution grid ─────────────────────────────────────────────────
/**
 * Renders clickable date chips for a member's per-day slots.
 * Green = paid, Red = missed. Click toggles between the two.
 * Mirrors real IContribution per-day tracking in the live platform.
 */
function DayContributionGrid({
    member,
    circleId,
    isReadOnly,
    locale,
}: {
    member: ISandboxMember;
    circleId: string;
    isReadOnly: boolean;
    locale: string;
}) {
    const dispatch = useDispatch<AppDispatch>();
    const isMemberUpdating = useSelector(selectSandboxIsMemberUpdating);
    const { t } = useTranslation("sandbox");

    const total = member.dailyContributions.length;
    const paidCount = member.dailyContributions.filter((d) => d.status === "paid").length;

    const handleDayClick = (day: ISandboxDailyContribution) => {
        if (isReadOnly || isMemberUpdating) return;
        // Simple toggle: paid ↔ missed
        const nextStatus: SandboxDayStatus = day.status === "paid" ? "missed" : "paid";
        dispatch(
            updateSandboxMemberDayStatus({
                sandboxCircleId: circleId,
                memberId: member.id,
                date: day.date,
                dayStatus: nextStatus,
            }),
        );
    };

    if (total === 0) return null;

    const chipColors: Record<SandboxDayStatus, { bg: string; text: string; border: string }> = {
        paid: { bg: "#e8f5e9", text: "#1a7a4a", border: "#a5d6a7" },
        missed: { bg: "#ffebee", text: "#c62828", border: "#ef9a9a" },
    };

    return (
        <Box sx={{ mt: 1.5, mb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: 1 }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: paidCount === total
                            ? "#1a7a4a"
                            : paidCount === 0
                                ? "#cc0000"
                                : "#b76e00",
                    }}>
                    {t("dashboard.memberDaysPaid", { paid: paidCount, total })}
                </Typography>
                {!isReadOnly && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.68rem",
                            color: "#9aa3ad",
                        }}>
                        {t("dashboard.memberClickToggle")}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {member.dailyContributions.map((day) => {
                    const [year, month, dayNum] = day.date.split("-").map(Number);
                    const dateObj = new Date(year, month - 1, dayNum);
                    const label = dateObj.toLocaleDateString(locale, {
                        month: "short",
                        day: "numeric",
                    });
                    const statusLabel = day.status === "paid"
                        ? t("dashboard.dayStatusPaid")
                        : t("dashboard.dayStatusMissed");
                    const c = chipColors[day.status] ?? chipColors.missed;
                    return (
                        <Box
                            key={day.date}
                            onClick={isReadOnly ? undefined : () => handleDayClick(day)}
                            title={
                                isReadOnly
                                    ? statusLabel
                                    : t("dashboard.dayToggleTooltip", { date: label, status: statusLabel })
                            }
                            sx={{
                                backgroundColor: c.bg,
                                color: c.text,
                                border: `1px solid ${c.border}`,
                                borderRadius: "12px",
                                padding: "3px 10px",
                                fontSize: "0.7rem",
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                                cursor: isReadOnly ? "default" : "pointer",
                                transition: "opacity 0.15s",
                                "&:hover": !isReadOnly ? { opacity: 0.7 } : {},
                                userSelect: "none",
                            }}>
                            {label}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ── Stat overview row ─────────────────────────────────────────────────────
function OverviewStats({
    circle,
    locale,
    currency,
}: {
    circle: ISandboxCircle;
    locale: string;
    currency: string;
}) {
    const { t } = useTranslation("sandbox");
    const fmt = (n: number) => formatCurrency(n, currency, locale);
    const freqLabel = () => {
        const map: Record<string, string> = {
            Weekly: t("dashboard.freqWeekly"),
            "Bi-Weekly": t("dashboard.freqBiWeekly"),
            Monthly: t("dashboard.freqMonthly"),
        };
        return map[circle.contributionFrequency] ?? circle.contributionFrequency;
    };
    // Average adjusted payout — recalculates live based on current member statuses.
    const estimatedPayout =
        circle.rounds.length > 0
            ? parseFloat(
                (
                    circle.rounds.reduce((sum, r) => {
                        const { adjustedPayout } = computeAdjustedRoundPayout(
                            r, circle.members, circle.platformFeePercentage,
                        );
                        return sum + adjustedPayout;
                    }, 0) / circle.rounds.length
                ).toFixed(2),
            )
            : parseFloat(
                (
                    circle.contributionAmount *
                    circle.memberCount *
                    (1 - circle.platformFeePercentage / 100)
                ).toFixed(2),
            );

    return (
        <Grid container spacing={2} sx={{ marginBottom: "24px" }}>
            {[
                {
                    icon: <People sx={{ color: colors.mainSecondary, fontSize: 22 }} />,
                    value: String(circle.memberCount),
                    label: t("dashboard.statMembers"),
                },
                {
                    icon: (
                        <AttachMoney sx={{ color: colors.mainSecondary, fontSize: 22 }} />
                    ),
                    value: fmt(circle.contributionAmount),
                    label: t("dashboard.statPerContribution"),
                },
                {
                    icon: <Repeat sx={{ color: colors.mainSecondary, fontSize: 22 }} />,
                    value: freqLabel(),
                    label: t("dashboard.statFrequency"),
                },
                {
                    icon: (
                        <CalendarMonth sx={{ color: colors.mainSecondary, fontSize: 22 }} />
                    ),
                    value: fmt(estimatedPayout),
                    label: t("dashboard.statAvgPayout"),
                },
            ].map((stat, i) => (
                <Grid item xs={6} sm={3} key={i}>
                    <SandboxStatCard>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                marginBottom: "6px",
                            }}>
                            {stat.icon}
                        </Box>
                        <SandboxStatValue>{stat.value}</SandboxStatValue>
                        <SandboxStatLabel>{stat.label}</SandboxStatLabel>
                    </SandboxStatCard>
                </Grid>
            ))}
        </Grid>
    );
}

// ── Members panel ─────────────────────────────────────────────────────────
function MembersPanel({
    circle,
    isReadOnly,
    locale,
    currency,
}: {
    circle: ISandboxCircle;
    isReadOnly: boolean;
    locale: string;
    currency: string;
}) {
    const dispatch = useDispatch<AppDispatch>();
    const isMemberUpdating = useSelector(selectSandboxIsMemberUpdating);
    const { t } = useTranslation("sandbox");
    const fmt = (n: number) => formatCurrency(n, currency, locale);

    // Track which members have their day grid expanded
    const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());

    const toggleMemberExpanded = (memberId: string) => {
        setExpandedMembers((prev) => {
            const next = new Set(prev);
            if (next.has(memberId)) next.delete(memberId);
            else next.add(memberId);
            return next;
        });
    };

    const sortedMembers = [...circle.members].sort(
        (a, b) => a.payoutPosition - b.payoutPosition,
    );

    const handleStatusChange = (
        member: ISandboxMember,
        newStatus: SandboxContributionStatus,
    ) => {
        if (isReadOnly || isMemberUpdating) return;
        dispatch(
            updateSandboxMemberStatus({
                sandboxCircleId: circle._id,
                memberId: member.id,
                contributionStatus: newStatus,
            }),
        );
    };

    return (
        <SandboxSectionCard>
            <SandboxSectionTitle>{t("dashboard.membersTitle")}</SandboxSectionTitle>
            {!isReadOnly && (
                <SandboxDisclaimer>
                    <Info
                        sx={{ color: colors.mainSecondary, fontSize: 18, marginTop: "1px" }}
                    />
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.82rem",
                            color: "#4a5568",
                            lineHeight: 1.6,
                        }}>
                        <strong>{t("dashboard.simulateNoteBold")}</strong>{" "}
                        {t("dashboard.simulateNote")}
                    </Typography>
                </SandboxDisclaimer>
            )}

            {/* Status legend */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "16px",
                    padding: "10px 14px",
                    backgroundColor: "rgba(4,13,69,0.03)",
                    borderRadius: "8px",
                    border: "1px solid rgba(4,13,69,0.07)",
                }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        color: colors.primary,
                        width: "100%",
                        marginBottom: "4px",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                    }}>
                    {t("dashboard.legendTitle")}
                </Typography>
                {(["completed", "missed"] as const).map((status) => {
                    const color = status === "completed" ? "#1a7a4a" : "#cc0000";
                    const label = status === "completed" ? t("dashboard.legendCompleted") : t("dashboard.legendMissed");
                    const note = status === "completed" ? t("dashboard.legendCompletedNote") : t("dashboard.legendMissedNote");
                    return (
                        <Box key={status} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", color: "#4a5568" }}>
                                <strong style={{ color }}>{label}</strong> — {note}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {isMemberUpdating && (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "8px" }}>
                    <CircularProgress size={20} sx={{ color: colors.mainSecondary }} />
                </Box>
            )}

            {sortedMembers.map((member) => {
                const displayName = getMemberDisplayName(member, t);
                const isLeader = member.payoutPosition === circle.memberCount;
                const representativeRound = circle.rounds[0];
                const perRoundTotal = representativeRound
                    ? parseFloat((representativeRound.expectedPoolAmount / circle.memberCount).toFixed(2))
                    : member.sampleContributionAmount;
                const affectsPool =
                    member.contributionStatus === "missed";

                const hasDays = (member.dailyContributions?.length ?? 0) > 0;
                const isExpanded = expandedMembers.has(member.id);
                const paidDays = member.dailyContributions?.filter((d) => d.status === "paid").length ?? 0;
                const missedDays = member.dailyContributions?.filter((d) => d.status === "missed").length ?? 0;
                const totalDays = member.dailyContributions?.length ?? 0;

                // Color: green = all paid, amber = partial, red = any missed
                const dayCountColor =
                    paidDays === totalDays && totalDays > 0
                        ? "#1a7a4a"
                        : paidDays > 0 && missedDays > 0
                            ? "#b76e00"
                            : "#cc0000";

                // Border color for the expanded day-grid panel
                const dayGridBorderColor =
                    paidDays === totalDays && totalDays > 0
                        ? "#a5d6a7"
                        : paidDays > 0 && missedDays > 0
                            ? "#ffe0b2"
                            : "#ef9a9a";

                return (
                    <Box key={member.id} sx={{ marginBottom: "2px" }}>
                        <MemberRow>
                            <MemberInfo>
                                <MemberAvatar isLeader={isLeader}>
                                    {getMemberInitials(displayName)}
                                </MemberAvatar>
                                <Box>
                                    <MemberName>
                                        {displayName}
                                        {isLeader && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    marginLeft: "6px",
                                                    fontSize: "0.65rem",
                                                    fontWeight: 700,
                                                    color: colors.mainSecondary,
                                                    backgroundColor: "rgba(30,193,149,0.1)",
                                                    padding: "1px 6px",
                                                    borderRadius: "4px",
                                                    verticalAlign: "middle",
                                                    fontFamily: "'Montserrat', sans-serif",
                                                }}>
                                                {t("dashboard.memberLeader")}
                                            </Box>
                                        )}
                                    </MemberName>
                                    <MemberSubtext>
                                        {t("dashboard.memberPayoutSlot", { position: member.payoutPosition })} ·{" "}
                                        {fmt(member.sampleContributionAmount)}/{t("dashboard.perEvent")}
                                        {hasDays && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    marginLeft: "4px",
                                                    color: dayCountColor,
                                                    fontWeight: 600,
                                                }}>
                                                · {t("dashboard.memberDaysPaid", { paid: paidDays, total: totalDays })}
                                            </Box>
                                        )}
                                        {!hasDays && perRoundTotal !== member.sampleContributionAmount && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: affectsPool ? "#cc0000" : "#6c757d",
                                                    marginLeft: "4px",
                                                }}>
                                                ({t("dashboard.memberRoundTotal", { amount: fmt(perRoundTotal) })}
                                                {affectsPool ? ` — ${t("dashboard.memberExcludedPool")}` : ""})
                                            </Box>
                                        )}
                                        {member.sampleLateFeeAmount > 0 && (
                                            <Box
                                                component="span"
                                                sx={{ color: "#b76e00", marginLeft: "4px" }}>
                                                {" "}
                                                {t("dashboard.memberLateFee", { amount: fmt(member.sampleLateFeeAmount) })}
                                            </Box>
                                        )}
                                    </MemberSubtext>
                                </Box>
                            </MemberInfo>

                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                                {isReadOnly ? (
                                    <StatusChip
                                        label={member.contributionStatus === "completed" ? t("dashboard.statusCompleted") : t("dashboard.statusMissed")}
                                        statustype={member.contributionStatus}
                                        size="small"
                                    />
                                ) : (
                                    <Select
                                        value={member.contributionStatus}
                                        onChange={(e) =>
                                            handleStatusChange(
                                                member,
                                                e.target.value as SandboxContributionStatus,
                                            )
                                        }
                                        size="small"
                                        disabled={isMemberUpdating}
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.8rem",
                                            fontWeight: 600,
                                            minWidth: { xs: "100%", sm: "130px" },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "rgba(0,0,0,0.15)",
                                            },
                                        }}>
                                        {CONTRIBUTION_STATUS_OPTIONS.map((s) => (
                                            <MenuItem
                                                key={s}
                                                value={s}
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.8rem",
                                                }}>
                                                {s === "completed" ? t("dashboard.statusCompleted") : t("dashboard.statusMissed")}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                                {hasDays && (
                                    <Tooltip title={isExpanded ? t("dashboard.memberHideDays") : t("dashboard.memberShowDays")}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleMemberExpanded(member.id)}
                                            sx={{
                                                color: colors.primary,
                                                backgroundColor: isExpanded
                                                    ? "rgba(4,13,69,0.07)"
                                                    : "rgba(4,13,69,0.03)",
                                                "&:hover": { backgroundColor: "rgba(4,13,69,0.1)" },
                                                padding: "4px",
                                            }}>
                                            {isExpanded ? (
                                                <ExpandLess sx={{ fontSize: 18 }} />
                                            ) : (
                                                <ExpandMore sx={{ fontSize: 18 }} />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </MemberRow>

                        {/* Per-day contribution grid — collapsible */}
                        {hasDays && isExpanded && (
                            <Box
                                sx={{
                                    paddingLeft: "52px",
                                    paddingRight: "12px",
                                    paddingBottom: "14px",
                                    backgroundColor: "rgba(4,13,69,0.015)",
                                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                                    borderLeft: `3px solid ${dayGridBorderColor}`,
                                }}>
                                <DayContributionGrid
                                    member={member}
                                    circleId={circle._id}
                                    isReadOnly={isReadOnly}
                                    locale={locale}
                                />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </SandboxSectionCard>
    );
}

// ── Payout schedule panel ─────────────────────────────────────────────────
function PayoutSchedulePanel({
    circle,
    locale,
    currency,
    dayMap,
}: {
    circle: ISandboxCircle;
    locale: string;
    currency: string;
    dayMap: Record<string, string>;
}) {
    const { t } = useTranslation("sandbox");
    const fmt = (n: number) => formatCurrency(n, currency, locale);
    const fmtDate = (d: string) => formatDate(d, locale);
    const memberMap = Object.fromEntries(circle.members.map((m) => [m.id, m]));

    const sortedRounds = [...circle.rounds].sort(
        (a, b) => a.roundNumber - b.roundNumber,
    );

    // Fee breakdown for the callout — use averages across all rounds so the
    // numbers match the "Avg. Payout / Turn" stat card and avoid confusion.
    const hasRounds = sortedRounds.length > 0;
    // Adjusted averages — live recalculation based on current member statuses.
    const adjustedRounds = sortedRounds.map((r) =>
        computeAdjustedRoundPayout(r, circle.members, circle.platformFeePercentage),
    );
    const avgPool = hasRounds
        ? parseFloat((adjustedRounds.reduce((s, r) => s + r.adjustedPool, 0) / adjustedRounds.length).toFixed(2))
        : circle.contributionAmount * circle.memberCount;
    const avgFee = hasRounds
        ? parseFloat((adjustedRounds.reduce((s, r) => s + r.adjustedFee, 0) / adjustedRounds.length).toFixed(2))
        : 0;
    const avgPayout = hasRounds
        ? parseFloat((adjustedRounds.reduce((s, r) => s + r.adjustedPayout, 0) / adjustedRounds.length).toFixed(2))
        : avgPool;
    // Range for the variance note (using adjusted values)
    const minPayout = hasRounds ? Math.min(...adjustedRounds.map((r) => r.adjustedPayout)) : avgPayout;
    const maxPayout = hasRounds ? Math.max(...adjustedRounds.map((r) => r.adjustedPayout)) : avgPayout;
    const payoutsVary = minPayout !== maxPayout;
    // Shortfall note when any member is missing contributions
    const hasAnyShortfall = adjustedRounds.some((r) => r.shortfall > 0);
    // Keep exampleRound reference for the conditional guard
    const exampleRound = hasRounds ? sortedRounds[0] : null;

    return (
        <SandboxSectionCard>
            <SandboxSectionTitle>{t("dashboard.payoutTitle")}</SandboxSectionTitle>

            {/* Leader-last trust message */}
            <Box
                sx={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    backgroundColor: "rgba(4,13,69,0.04)",
                    border: "1px solid rgba(4,13,69,0.1)",
                    borderLeft: `4px solid ${colors.primary}`,
                    borderRadius: "8px",
                    padding: "14px 16px",
                    marginBottom: "20px",
                }}>
                <Box
                    sx={{
                        minWidth: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: colors.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}>
                    <Typography sx={{ fontSize: "1rem" }}>🤝</Typography>
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: colors.primary,
                            marginBottom: "4px",
                            letterSpacing: "0.01em",
                        }}>
                        {t("dashboard.leaderLastTitle")}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.82rem",
                            color: "#4a5568",
                            lineHeight: 1.65,
                        }}>
                        {t("dashboard.leaderLastDesc")}
                    </Typography>
                </Box>
            </Box>

            {/* Fee breakdown callout */}
            <Box
                sx={{
                    backgroundColor: "rgba(30,193,149,0.06)",
                    border: "1px solid rgba(30,193,149,0.25)",
                    borderLeft: `4px solid ${colors.mainSecondary}`,
                    borderRadius: "8px",
                    padding: "14px 16px",
                    marginBottom: "20px",
                }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        color: colors.primary,
                        marginBottom: "8px",
                        letterSpacing: "0.01em",
                    }}>
                    {t("dashboard.howCalculatedTitle")}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.82rem",
                        color: "#4a5568",
                        lineHeight: 1.7,
                        marginBottom: "10px",
                    }}>
                    {t("dashboard.howCalculatedDesc", {
                        amount: fmt(circle.contributionAmount),
                        count: circle.memberCount,
                        days: circle.contributionDays && circle.contributionDays.length > 0
                            ? circle.contributionDays.map((d) => dayMap[d] ?? d).join(", ")
                            : "contribution day",
                        fee: circle.platformFeePercentage,
                    })}
                    {hasAnyShortfall && (
                        <>
                            {" "}⚠️ <strong>{t("dashboard.shortfallNote")}</strong>
                        </>
                    )}
                    {payoutsVary && (
                        <>
                            {" "}
                            {t("dashboard.payoutsVaryNote", {
                                min: fmt(minPayout),
                                max: fmt(maxPayout),
                            })}
                        </>
                    )}
                </Typography>
                {exampleRound && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                            gap: "8px",
                        }}>
                        {[
                            { label: t("dashboard.avgGrossPool"), value: fmt(avgPool), note: t("dashboard.avgGrossPoolNote") },
                            { label: t("dashboard.platformFee", { pct: circle.platformFeePercentage }), value: `− ${fmt(avgFee)}`, note: t("dashboard.platformFeeNote"), muted: true },
                            { label: t("dashboard.avgNetPayout"), value: fmt(avgPayout), note: t("dashboard.avgNetPayoutNote"), highlight: true },
                        ].map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    backgroundColor: item.highlight
                                        ? "rgba(30,193,149,0.12)"
                                        : "rgba(255,255,255,0.8)",
                                    border: `1px solid ${item.highlight ? "rgba(30,193,149,0.3)" : "rgba(0,0,0,0.07)"}`,
                                    borderRadius: "6px",
                                    padding: "10px 12px",
                                    textAlign: "center",
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "1rem",
                                        color: item.highlight
                                            ? colors.mainSecondary
                                            : item.muted
                                                ? "#cc4444"
                                                : colors.primary,
                                    }}>
                                    {item.value}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "0.72rem",
                                        color: colors.primary,
                                        marginTop: "2px",
                                    }}>
                                    {item.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.68rem",
                                        color: "#9aa3ad",
                                    }}>
                                    {item.note}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {sortedRounds.map((round) => {
                const recipient = memberMap[round.payoutRecipientMemberId];
                const recipientName = recipient
                    ? getMemberDisplayName(recipient, t)
                    : "—";
                return (
                    <RoundRow key={round.id} isNextUp={round.isNextUp}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <RoundNumber>{round.roundNumber}</RoundNumber>
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            fontSize: "0.9375rem",
                                            color: "#2d3748",
                                        }}>
                                        {recipientName}
                                    </Typography>
                                    {round.isNextUp && <NextUpBadge>{t("dashboard.nextUp")}</NextUpBadge>}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.75rem",
                                        color: "#6c757d",
                                    }}>
                                    {fmtDate(round.dueDate)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                flexWrap: "wrap",
                                width: { xs: "100%", sm: "auto" },
                                justifyContent: { xs: "space-between", sm: "flex-end" },
                            }}>
                            <Box sx={{ textAlign: "right" }}>
                                {(() => {
                                    const adj = computeAdjustedRoundPayout(
                                        round, circle.members, circle.platformFeePercentage,
                                    );
                                    const hasShortfall = adj.shortfall > 0;
                                    return (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 800,
                                                    fontSize: "1rem",
                                                    color: hasShortfall ? "#cc4444" : colors.primary,
                                                }}>
                                                {fmt(adj.adjustedPayout)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.7rem",
                                                    color: "#9aa3ad",
                                                }}>
                                                {t("dashboard.afterFee", { fee: fmt(adj.adjustedFee) })}
                                            </Typography>
                                            {hasShortfall && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontSize: "0.68rem",
                                                        color: "#cc4444",
                                                        fontWeight: 600,
                                                    }}>
                                                    {t("dashboard.shortfall", { amount: fmt(adj.shortfall) })}
                                                </Typography>
                                            )}
                                        </>
                                    );
                                })()}
                            </Box>
                            <StatusChip
                                label={(() => {
                                    const map: Record<string, string> = {
                                        COMPLETED: t("dashboard.roundCompleted"),
                                        DUE_TODAY: t("dashboard.roundDueToday"),
                                        UPCOMING: t("dashboard.roundUpcoming"),
                                        MISSED: t("dashboard.roundMissed"),
                                    };
                                    return map[round.status] ?? round.status;
                                })()}
                                statustype={round.status}
                                size="small"
                            />
                        </Box>
                    </RoundRow>
                );
            })}
        </SandboxSectionCard>
    );
}

// ── Share button + copy ────────────────────────────────────────────────────
function ShareSection({ circle }: { circle: ISandboxCircle }) {
    const dispatch = useDispatch<AppDispatch>();
    const isSharing = useSelector(selectSandboxIsSharing);
    const shareResult = useSelector(selectSandboxShareResult);
    const [copied, setCopied] = useState(false);
    const { t } = useTranslation("sandbox");

    const shareUrl =
        shareResult?.shareUrl ??
        (circle.shareToken
            ? `${window.location.origin}/sandbox/share/${circle.shareToken}`
            : null);

    const handleShare = () => {
        if (shareUrl) {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
            });
        } else {
            dispatch(createSandboxShareLink(circle._id));
        }
    };

    return (
        <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Tooltip
                title={
                    copied
                        ? t("dashboard.tooltipCopied")
                        : shareUrl
                            ? t("dashboard.tooltipCopyLink")
                            : t("dashboard.tooltipGenerate")
                }>
                <span>
                    <IconButton
                        onClick={handleShare}
                        disabled={isSharing}
                        sx={{
                            border: "1.5px solid rgba(0,0,0,0.12)",
                            borderRadius: "10px",
                            padding: "8px 14px",
                            gap: "6px",
                            color: colors.primary,
                            "&:hover": { backgroundColor: "rgba(30,193,149,0.06)" },
                        }}>
                        {isSharing ? (
                            <CircularProgress size={16} />
                        ) : copied ? (
                            <CheckCircle sx={{ fontSize: 18, color: colors.mainSecondary }} />
                        ) : (
                            <>
                                <Share sx={{ fontSize: 18 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                    }}>
                                    {shareUrl ? t("dashboard.copyLink") : t("dashboard.shareDemo")}
                                </Typography>
                            </>
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}

// ── Main dashboard component ──────────────────────────────────────────────
interface SandboxDashboardProps {
    isReadOnly?: boolean;
}

const SandboxDashboard: React.FC<SandboxDashboardProps> = ({
    isReadOnly = false,
}) => {
    const navigate = useNavigate();
    const circle = useSelector(selectSandboxCircle);
    const [activeTab, setActiveTab] = useState(0);
    const { t, i18n: i18nInstance } = useTranslation("sandbox");
    const language = getSupportedLanguage(i18nInstance.resolvedLanguage || i18nInstance.language);
    const locale = getLocaleFromLanguage(language);

    const freqMap: Record<string, string> = {
        Weekly: t("dashboard.freqWeekly"),
        "Bi-Weekly": t("dashboard.freqBiWeekly"),
        Monthly: t("dashboard.freqMonthly"),
    };
    const dayMap: Record<string, string> = {
        Monday: t("dashboard.dayMonday"),
        Tuesday: t("dashboard.dayTuesday"),
        Wednesday: t("dashboard.dayWednesday"),
        Thursday: t("dashboard.dayThursday"),
        Friday: t("dashboard.dayFriday"),
        Saturday: t("dashboard.daySaturday"),
        Sunday: t("dashboard.daySunday"),
    };

    if (!circle) return null;

    const currency = getDisplayCurrency(circle, language);
    const country = getDisplayCountry(circle, language);
    const circleName = getCircleDisplayName(circle, t);

    return (
        <Box>
            {/* ── Overview stats ── */}
            <OverviewStats circle={circle} locale={locale} currency={currency} />

            {/* ── Circle title + share ── */}
            <SandboxSectionCard>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: "12px",
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.3rem" },
                                fontWeight: 800,
                                color: colors.primary,
                                marginBottom: "4px",
                            }}>
                            {circleName}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.85rem",
                                color: "#6c757d",
                            }}>
                            {t("dashboard.circleSubtitle", {
                                count: circle.memberCount,
                                date: formatDate(circle.startDate, locale),
                                frequency: freqMap[circle.contributionFrequency] ?? circle.contributionFrequency,
                            })}
                            {country ? ` · ${country}` : ""}
                            {currency ? ` · ${currency.toUpperCase()}` : ""}
                            {circle.contributionDays && circle.contributionDays.length > 0
                                ? ` · ${circle.contributionDays.map((d) => dayMap[d] ?? d).join(", ")}`
                                : ""}
                        </Typography>
                    </Box>
                    {!isReadOnly && <ShareSection circle={circle} />}
                </Box>
            </SandboxSectionCard>

            {/* ── Tabs: Members / Payout Schedule ── */}
            <Box sx={{ marginBottom: "8px" }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        "& .MuiTabs-indicator": { backgroundColor: colors.mainSecondary },
                        "& .MuiTab-root": {
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            color: "#6c757d",
                        },
                        "& .Mui-selected": { color: `${colors.primary} !important` },
                    }}>
                    <Tab label={t("dashboard.tabMembers")} />
                    <Tab label={t("dashboard.tabPayoutSchedule")} />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <MembersPanel
                    circle={circle}
                    isReadOnly={isReadOnly}
                    locale={locale}
                    currency={currency}
                />
            )}
            {activeTab === 1 && (
                <PayoutSchedulePanel
                    circle={circle}
                    locale={locale}
                    currency={currency}
                    dayMap={dayMap}
                />
            )}

            {/* ── CTA section ── */}
            <SandboxCTASection>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "rgba(30,193,149,0.18)",
                        color: colors.mainSecondary,
                        padding: "4px 14px",
                        borderRadius: "20px",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        fontFamily: "'Montserrat', sans-serif",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        marginBottom: "16px",
                    }}>
                    {t("dashboard.sandboxBadge")}
                </Box>
                <CTATitle>{t("dashboard.ctaTitle")}</CTATitle>
                <CTASubtitle>
                    {t("dashboard.ctaSubtitle")}
                </CTASubtitle>
                <Box
                    sx={{
                        display: "flex",
                        gap: "14px",
                        justifyContent: "center",
                        flexWrap: "wrap",
                    }}>
                    <CTAButton
                        endIcon={<ArrowForward />}
                        onClick={() => navigate("/circle/sign-up")}>
                        {t("dashboard.ctaCreateAccount")}
                    </CTAButton>
                    <CTASecondaryButton
                        variant="outlined"
                        onClick={() => navigate("/circle/sign-in")}>
                        {t("dashboard.ctaSignIn")}
                    </CTASecondaryButton>
                </Box>
            </SandboxCTASection>

            {/* ── Feedback card ── */}
            <SandboxFeedbackCard
                sandboxCircleId={circle?._id}
                anonymousSessionId={circle?.createdByAnonymousSessionId ?? undefined}
                locale={language}
            />
        </Box>
    );
};

// ── Sandbox Feedback Card ──────────────────────────────────────────────────

const FEEDBACK_OPTION_KEYS = [
    "optPayments",
    "optGroup",
    "optLate",
    "optStop",
    "optTrust",
    "optNoCircle",
    "optFounder",
    "optOther",
] as const;

type FeedbackOptionKey = typeof FEEDBACK_OPTION_KEYS[number];

interface SandboxFeedbackCardProps {
    sandboxCircleId?: string;
    anonymousSessionId?: string;
    locale?: string;
}

const SandboxFeedbackCard: React.FC<SandboxFeedbackCardProps> = ({
    sandboxCircleId,
    anonymousSessionId,
    locale,
}) => {
    const { t } = useTranslation("sandbox");
    const [selected, setSelected] = useState<Set<FeedbackOptionKey>>(new Set());
    const [otherText, setOtherText] = useState("");
    const [status, setStatus] = useState<"idle" | "submitting" | "done" | "skipped">("idle");

    const toggle = (key: FeedbackOptionKey) =>
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    const handleSubmit = async () => {
        if (selected.size === 0) return;
        setStatus("submitting");
        try {
            const selections = Array.from(selected).map((k) => t(`feedback.${k}`));
            await axios.post(`${API_BASE_URL}/sandbox/feedback`, {
                selections,
                otherText: selected.has("optOther") ? otherText.trim() || undefined : undefined,
                sandboxCircleId,
                anonymousSessionId,
                locale,
            });
            setStatus("done");
        } catch {
            // best-effort — still show done so user isn't confused
            setStatus("done");
        }
    };

    if (status === "done" || status === "skipped") {
        return status === "done" ? (
            <Box
                sx={{
                    mt: 4,
                    p: "28px 24px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%)",
                    border: "1px solid #86efac",
                    textAlign: "center",
                }}>
                <Typography sx={{ fontSize: "1.3rem", mb: "6px" }}>🎉</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#166534", fontFamily: "'Montserrat',sans-serif" }}>
                    {t("feedback.thankYou")}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#15803d", mt: "4px" }}>
                    {t("feedback.thankYouSub")}
                </Typography>
            </Box>
        ) : null;
    }

    return (
        <Box
            sx={{
                mt: 4,
                p: "28px 24px",
                borderRadius: "16px",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}>
            <Typography
                sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#111827",
                    fontFamily: "'Montserrat',sans-serif",
                    mb: "18px",
                    lineHeight: 1.5,
                }}>
                {t("feedback.question")}
            </Typography>

            <FormGroup>
                {FEEDBACK_OPTION_KEYS.map((key) => (
                    <FormControlLabel
                        key={key}
                        control={
                            <Checkbox
                                checked={selected.has(key)}
                                onChange={() => toggle(key)}
                                size="small"
                                sx={{ color: colors.mainSecondary, "&.Mui-checked": { color: colors.mainSecondary } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
                                {t(`feedback.${key}`)}
                            </Typography>
                        }
                        sx={{ mb: "2px" }}
                    />
                ))}
            </FormGroup>

            <Collapse in={selected.has("optOther")} unmountOnExit>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t("feedback.otherPlaceholder")}
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    sx={{ mt: "12px", mb: "4px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
            </Collapse>

            <Box sx={{ display: "flex", gap: "10px", mt: "18px", flexWrap: "wrap" }}>
                <Button
                    variant="contained"
                    disabled={selected.size === 0 || status === "submitting"}
                    onClick={handleSubmit}
                    sx={{
                        background: colors.mainSecondary,
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        px: "20px",
                        "&:hover": { background: "#17a47a" },
                        "&.Mui-disabled": { background: "#d1d5db", color: "#9ca3af" },
                    }}>
                    {status === "submitting" ? t("feedback.submitting") : t("feedback.submit")}
                </Button>
                <Button
                    variant="text"
                    onClick={() => setStatus("skipped")}
                    sx={{ textTransform: "none", color: "#9ca3af", fontSize: "0.85rem" }}>
                    {t("feedback.skip")}
                </Button>
            </Box>
        </Box>
    );
};

export default SandboxDashboard;
